import React, { useState } from 'react';
import axios from 'axios';
import { useNavigate, Link } from 'react-router-dom';
import Footer from './common/Footer';

const API_BASE_URL = process.env.REACT_APP_API_URL;

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [showTutorial, setShowTutorial] = useState(false);
  const [videoError, setVideoError] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Convert email to lowercase before sending the request
      const normalizedEmail = email.toLowerCase();
      const response = await axios.post(`${API_BASE_URL}/api/login`, {
        email: normalizedEmail,
        password,
      });
      
      if (response.data.subscriptionExpired) {
        setError('Your subscription has expired. Please contact the administrator.');
        return;
      }
      
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('email', normalizedEmail); // Store normalized email
      localStorage.setItem('role', response.data.role);
      localStorage.setItem('subscription_date', response.data.subscription_date);
      
      // Store subscription status
      if (response.data.subscription_date) {
        const isExpired = new Date(response.data.subscription_date) < new Date();
        localStorage.setItem('subscriptionExpired', isExpired.toString());
      } else if (response.data.role !== 'admin') {
        localStorage.setItem('subscriptionExpired', 'true');
      }
      
      if (response.data.role === 'admin') {
        navigate('/admin');
      } else {
        navigate('/dashboard');
      }
    } catch (err) {
      if (err.response?.data?.subscriptionExpired) {
        setError('Your subscription has expired. Please contact the administrator. (directory@defencepartners.in)');
        return;
      }
      if (err.code === 'ERR_NETWORK') {
        try {
          const response = await axios.post(`${API_BASE_URL}/api/login`, {
            email,
            password,
          });
          localStorage.setItem('token', response.data.token);
          localStorage.setItem('email', email);
          localStorage.setItem('role', response.data.role);
          
          if (response.data.role === 'admin') {
            navigate('/admin');
          } else {
            navigate('/dashboard');
          }
        } catch (retryErr) {
          setError(retryErr.response?.data?.error || 'An error occurred');
        }
      } else {
        setError(err.response?.data?.error || 'An error occurred');
      }
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-gray-900 relative overflow-hidden">
      <div className="flex-grow flex flex-col items-center py-12 px-4 sm:px-6 lg:px-8">
        {/* Logo Section */}
        <div className="w-full max-w-lg mb-12">
          <div className="flex flex-col items-center relative z-10">
            <img
              src="/images/imr-logo.png"
              alt="IMR Logo"
              className="h-32 w-auto mb-4"
            />
            <h1 className="text-center text-2xl font-bold text-yellow-500">
              DEFENCE INDUSTRY DIRECTORY
            </h1>
          </div>
        </div>

        {/* Tutorial Button */}
        <div className="w-full max-w-md mb-4 relative z-10">
          <button
            onClick={() => setShowTutorial(true)}
            className="w-full flex justify-center items-center py-3 px-4 rounded-md shadow-sm text-sm font-medium text-gray-900 bg-yellow-500 hover:bg-yellow-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 transition-colors duration-200"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
            </svg>
            Watch How to Use Tutorial
          </button>
        </div>

        {/* Login Box Section */}
        <div className="w-full max-w-md relative z-10">
          <div className="bg-gray-800/90 shadow-lg rounded-lg p-8 border border-gray-700 backdrop-blur-sm">
            <h2 className="text-center text-xl font-bold text-yellow-500 mb-6">
              Sign in to your account
            </h2>
            
            <form className="space-y-6" onSubmit={handleSubmit}>
              <div className="space-y-4">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-1">
                    Email address
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    className="appearance-none relative block w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-md text-gray-200 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent transition-colors duration-200"
                    placeholder="Email address"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </div>
                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-gray-300 mb-1">
                    Password
                  </label>
                  <input
                    id="password"
                    name="password"
                    type="password"
                    required
                    className="appearance-none relative block w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-md text-gray-200 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent transition-colors duration-200"
                    placeholder="Password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                </div>
              </div>

              {error && (
                <div className="p-4 bg-red-900/50 border border-red-700 rounded-md text-red-200 text-sm">
                  {error}
                </div>
              )}

              <div>
                <button
                  type="submit"
                  className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-gray-900 bg-yellow-500 hover:bg-yellow-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 transition-colors duration-200"
                >
                  Sign in
                </button>
              </div>

              <div className="flex flex-col space-y-3 text-sm text-center">
                <Link
                  to="/forgot-password"
                  className="font-medium text-yellow-500 hover:text-yellow-400 transition-colors duration-200"
                >
                  Forgot your password?
                </Link>
                <Link
                  to="/register"
                  className="font-medium text-yellow-500 hover:text-yellow-400 transition-colors duration-200"
                >
                  Don't have an account? Register here
                </Link>
              </div>
            </form>
          </div>
        </div>

        {/* Tutorial Modal */}
        {showTutorial && (
          <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
            <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
              <div className="fixed inset-0 bg-gray-900 bg-opacity-75 transition-opacity" aria-hidden="true" onClick={() => setShowTutorial(false)}></div>

              <div className="inline-block align-bottom bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-3xl sm:w-full">
                <div className="bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <div className="sm:flex sm:items-start">
                    <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                      <h3 className="text-lg leading-6 font-medium text-yellow-500 mb-4">
                        How to Use Our System
                      </h3>
                      <div className="mt-2 relative aspect-video">
                        {videoError ? (
                          <div className="flex flex-col items-center justify-center h-full bg-gray-700 rounded-lg p-4">
                            <p className="text-gray-400 mb-2">Unable to load the tutorial video.</p>
                            <button
                              onClick={() => {
                                setVideoError(false);
                                const video = document.querySelector('#tutorial-video');
                                if (video) {
                                  video.load();
                                }
                              }}
                              className="px-4 py-2 bg-yellow-500 text-gray-900 rounded hover:bg-yellow-600 transition-colors duration-200"
                            >
                              Try Again
                            </button>
                          </div>
                        ) : (
                          <video
                            id="tutorial-video"
                            className="w-full rounded-lg"
                            controls
                            controlsList="nodownload"
                            autoPlay
                            preload="auto"
                            onError={(e) => {
                              console.error('Video error:', e);
                              setVideoError(true);
                            }}
                          >
                            <source 
                              src={`${API_BASE_URL}/uploads/guide.mp4`}
                              type="video/mp4"
                              onError={(e) => {
                                console.error('Source error:', e);
                                setVideoError(true);
                              }}
                            />
                            Your browser does not support the video tag.
                          </video>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="bg-gray-800 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  <button
                    type="button"
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-700 shadow-sm px-4 py-2 bg-gray-700 text-base font-medium text-gray-300 hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                    onClick={() => setShowTutorial(false)}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
};

export default Login;